// Backend-reachability bridge for offline recording (APP-6259).
//
// Thin IPC wrapper over the Rust `ConnectivityMonitor` (APP-6189): a one-shot
// snapshot read plus a transition subscription. Both are desktop-only — on web
// / non-Tauri they no-op so the caller can mount unconditionally. The Rust
// source of truth is `src-tauri/src/offline_recording/connectivity.rs`; keep
// the event name and payload shape in lockstep with it.

import type { OfflineConnectivityState } from '@/tauri/offline-recording/offline-recording';
import { isTauri } from '@/tauri/utils';
import { type EventCallback, listen } from '@tauri-apps/api/event';

/**
 * Emitted by the Rust monitor on every reachability transition. Payload is the
 * camelCase-serialized `ConnectivityChangedEvent` (`{ state }`).
 */
export const OFFLINE_CONNECTIVITY_CHANGED_EVENT =
  'offline_recording:connectivity_changed';
export const OFFLINE_AUTOMATIC_REDRIVE_READY_EVENT =
  'offline_recording:automatic_redrive_ready';

/**
 * `offline_connectivity_status` is a bare main-app command (no plugin prefix),
 * unlike the auth0-plugin `plugin:auth0|…` identity read.
 */
const CONNECTIVITY_STATUS_COMMAND = 'offline_connectivity_status';
const CONNECTIVITY_REFRESH_TARGET_COMMAND = 'offline_connectivity_refresh_target';

/**
 * Wakes the Rust monitor for an immediate re-probe (also a bare main-app
 * command). Fired on browser `online`/`offline` events so a network transition
 * is reflected at once instead of waiting for the next periodic probe.
 */
const CONNECTIVITY_NOTIFY_NETWORK_CHANGE_COMMAND =
  'offline_connectivity_notify_network_change';

export interface OfflineConnectivityChangedEvent {
  state: OfflineConnectivityState;
}

export interface OfflineAutomaticRedriveReadyEvent {
  targetGeneration: number;
}

export const connectivityBridge = {
  /**
   * Snapshot the current backend-reachability state for first-mount hydration.
   * Returns `null` outside the desktop runtime (offline recording is
   * desktop-only) — the caller keeps its default until the first real read.
   */
  async read(): Promise<OfflineConnectivityState | null> {
    if (!isTauri()) {
      return null;
    }
    const { invoke } = await import('@tauri-apps/api/core');
    return invoke<OfflineConnectivityState>(CONNECTIVITY_STATUS_COMMAND);
  },

  /**
   * Re-resolve the Rust monitor's probe target from the latest cached identity.
   * Login / account-switch sets the residency-pinned backend; logout clears it.
   *
   * `enabled` carries the `release-scribe-enable-offline-recording` umbrella
   * flag — the only path that ever arms the Rust probe loop. Passing `false`
   * clears the target, so the flag doubles as a fleet-wide probe kill switch
   * (no `HEAD /` traffic while the feature is off).
   */
  async refreshTarget(enabled: boolean): Promise<OfflineConnectivityState | null> {
    if (!isTauri()) {
      return null;
    }
    const { invoke } = await import('@tauri-apps/api/core');
    return invoke<OfflineConnectivityState>(CONNECTIVITY_REFRESH_TARGET_COMMAND, {
      enabled,
    });
  },

  /**
   * Wake the Rust monitor for an immediate re-probe. No-ops outside the desktop
   * runtime (offline recording is desktop-only). Fire-and-forget: the monitor
   * re-probes and publishes any transition via the `connectivity_changed`
   * event, so there is nothing to return.
   */
  async notifyNetworkChange(): Promise<void> {
    if (!isTauri()) {
      return;
    }
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke(CONNECTIVITY_NOTIFY_NETWORK_CHANGE_COMMAND);
  },
};

/**
 * Subscribe to backend-reachability transitions. Resolves to an unlisten
 * function; off-desktop it resolves to a no-op so cleanup is always safe.
 */
export async function listenOfflineConnectivityChanged(
  cb: EventCallback<OfflineConnectivityChangedEvent>
): Promise<() => void> {
  if (!isTauri()) {
    return () => {};
  }
  return listen<OfflineConnectivityChangedEvent>(OFFLINE_CONNECTIVITY_CHANGED_EVENT, cb);
}

/**
 * Subscribe to generation-correlated ready edges used only by the guarded
 * automatic re-drive. The native reconnect drain intentionally does not
 * consume this event because its state-only channel cannot reject stale
 * target generations.
 */
export async function listenOfflineAutomaticRedriveReady(
  cb: EventCallback<OfflineAutomaticRedriveReadyEvent>
): Promise<() => void> {
  if (!isTauri()) {
    return () => {};
  }
  return listen<OfflineAutomaticRedriveReadyEvent>(
    OFFLINE_AUTOMATIC_REDRIVE_READY_EVENT,
    cb
  );
}
